import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { realtimeSubscriptionPool } from '@/lib/realtimeSubscriptionPool';

interface Props {
  projectId: string;
}

const ProjectExitManager = ({ projectId }: Props) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    return () => {
      try {
        // Cancel project-scoped queries
        const keys = [
          'project-detail',
          'specs-data',
          'diary-data',
          'diary-specs',
          'diary-photos',
          'diary-pins',
          'pin-image-counts',
        ];
        
        // Use predicate to match all queries with prefix [key, projectId] regardless of additional params
        keys.forEach((k) => {
          const matchPredicate = (query: { queryKey: readonly unknown[] }) => {
            const key = query.queryKey;
            return Array.isArray(key) && key[0] === k && key[1] === projectId;
          };
          queryClient.cancelQueries({ predicate: matchPredicate });
          // Only invalidate INACTIVE queries - preserve cache for back navigation
          queryClient.invalidateQueries({ 
            predicate: (query) => matchPredicate(query) && !query.isActive()
          });
        });

        // Unsubscribe realtime channels that match our project-related tables
        const unsubCount = realtimeSubscriptionPool.unsubscribeByPattern(/^(public:(diary_pins|diary_spec_items|diary_floorplan_layers|diary_specs_layers|items|procurement_.*|payment_milestones))/);
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.debug('🧹 ProjectExitManager: invalidated queries and unsubscribed channels', { projectId, unsubCount });
        }
      } catch (e) {
        if (import.meta.env.DEV) {
          console.debug('⚠️ ProjectExitManager cleanup error', e);
        }
      }
    };
  }, [projectId, queryClient]);

  return null;
};

export default ProjectExitManager;
